import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ROUTER from "constants/router";
import { notification } from "reducer/home/action";

// ⚙️ Config Microsoft OAuth2
const msConfig = {
  clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
  tenantId: process.env.REACT_APP_AZURE_TENANT_ID,
  redirectUri: window.location.origin,
  scope: "openid profile email offline_access",
};

// 🔹 Hàm tạo random string và PKCE
const generateRandomString = (length) => {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const cryptoObj = window.crypto || window.msCrypto;
  const randomValues = new Uint8Array(length);
  cryptoObj.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  return result;
};

const sha256 = async (plain) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

// 🟦 Microsoft Icon
const MicrosoftIcon = ({ size = 24, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill={color} />
    <rect x="11" y="1" width="9" height="9" fill={color} />
    <rect x="1" y="11" width="9" height="9" fill={color} />
    <rect x="11" y="11" width="9" height="9" fill={color} />
  </svg>
);

// 🔹 Tạo URL đăng nhập Microsoft
const createAuthUrl = async (forceSelect = false) => {
  const verifier = generateRandomString(64);
  const challenge = await sha256(verifier);
  sessionStorage.setItem("pkce_verifier", verifier);

  const params = new URLSearchParams({
    client_id: msConfig.clientId,
    response_type: "code",
    redirect_uri: msConfig.redirectUri,
    response_mode: "query",
    scope: msConfig.scope,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  if (forceSelect) params.append("prompt", "select_account");

  return `https://login.microsoftonline.com/${msConfig.tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
};

// 🔹 Đổi authorization_code lấy token
const fetchAccessToken = async (code) => {
  const verifier = sessionStorage.getItem("pkce_verifier");
  const params = new URLSearchParams({
    client_id: msConfig.clientId,
    scope: msConfig.scope,
    code,
    redirect_uri: msConfig.redirectUri,
    grant_type: "authorization_code",
    code_verifier: verifier,
  });

  const res = await fetch(`https://login.microsoftonline.com/${msConfig.tenantId}/oauth2/v2.0/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params,
  });

  return res.json();
};

function decodeJWT(token) {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Token không hợp lệ");

  const header = JSON.parse(atob(parts[0].replace(/-/g, "+").replace(/_/g, "/")));
  const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));

  return { header, payload };
}

const MicrosoftLogin = ({ loginLDAP, history }) => {
  const [account, setAccount] = useState(null);
  const dispatch = useDispatch();

  // 🟩 Khi vào trang, nếu có token đã lưu thì lấy ra
  useEffect(() => {
    const acc = localStorage.getItem("ms_account");
    if (acc) setAccount(JSON.parse(acc));
  }, []);

  // 🟦 Đăng nhập popup (giống msalInstance.loginPopup)
  const handleLogin = async (forceSelect = false) => {
    const authUrl = await createAuthUrl(forceSelect);
    // console.log("Opening popup for URL:", authUrl);

    const popup = window.open(authUrl, "ms_login", "width=600,height=600,left=200,top=200");

    const checkPopup = setInterval(async () => {
      //   console.log("Checking popup for redirect...");
      try {
        // console.log("Popup location:", popup);
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          return;
        }

        const popupUrl = popup.location.href;
        // console.log("Popup URL:", popupUrl);
        if (popupUrl.startsWith(msConfig.redirectUri)) {
          const query = popup.location.search.substring(1);
          //   console.log("Popup redirected with hash:", query);
          const params = new URLSearchParams(query);
          //   console.log("Popup redirected with params:", params.toString());
          const code = params.get("code");
          //   console.log("Authorization code:", code);

          if (code) {
            popup.close();
            clearInterval(checkPopup);
            const tokenData = await fetchAccessToken(code);
            // console.log("🔐 Access token:", tokenData.access_token);

            const user = { username: tokenData.id_token, token: tokenData.access_token };
            localStorage.setItem("ms_account", JSON.stringify(user));
            setAccount(user);

            // gọi API loginLDAP như trước
            dispatch(loginLDAP({ code: tokenData.access_token })).then((res) => {
              if (res) {
                notification({ type: "success", message: "Đăng nhập thành công!" });
                history?.push(ROUTER.HOME);
              } else {
                notification({ type: "error", message: "Đăng nhập không thành công!" });
              }
            });
          }
        }
      } catch (err) {
        // ignore cross-origin errors until redirect back
      }
    }, 500);
  };

  //   const handleLogin = async (forceSelect = false) => {
  //     const authUrl = await createAuthUrl(forceSelect);
  //     console.log("Opening popup for URL:", authUrl);

  //     const popup = window.open(authUrl, "ms_login", "width=600,height=600,left=200,top=200");

  //     const messageHandler = async (event) => {
  //       if (event.data?.source === "microsoft-login") {
  //         const { code, error } = event.data;
  //         console.log("Received message from popup:", event.data);

  //         window.removeEventListener("message", messageHandler);
  //         if (popup && !popup.closed) popup.close();

  //         if (error) {
  //           console.error("Login error:", error);
  //           notification({ type: "error", message: "Đăng nhập thất bại!" });
  //           return;
  //         }

  //         if (code) {
  //           const tokenData = await fetchAccessToken(code);
  //           console.log("🔐 Access token:", tokenData.access_token);

  //           const user = { username: tokenData.id_token, token: tokenData.access_token };
  //           localStorage.setItem("ms_account", JSON.stringify(user));
  //           setAccount(user);

  //           // gọi API loginLDAP như trước
  //           dispatch(loginLDAP({ code: tokenData.access_token })).then((res) => {
  //             if (res) {
  //               notification({ type: "success", message: "Đăng nhập thành công!" });
  //               history?.push(ROUTER.HOME);
  //             } else {
  //               notification({ type: "error", message: "Đăng nhập không thành công!" });
  //             }
  //           });
  //         }
  //       }
  //     };

  //     window.addEventListener("message", messageHandler);
  //   };

  //   if (account) {
  //     console.log("Current MS account:", decodeJWT(account?.username));
  //   }

  const { header, payload } = account ? decodeJWT(account?.username) : { header: null, payload: null };

  // console.log("MSAL1 Render - Current account:", account, payload);

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        maxWidth: "400px",
        margin: "0 auto",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <h3
          style={{
            color: "#2c3e50",
            marginBottom: "8px",
            fontSize: "20px",
            fontWeight: "600",
          }}
        >
          Đăng nhập với tài khoản nội bộ
        </h3>
        <p
          style={{
            color: "#7f8c8d",
            fontSize: "14px",
            margin: "0",
          }}
        >
          Sử dụng tài khoản nội bộ của bạn để tiếp tục
        </p>
      </div>

      {account ? (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              border: "1px solid #e9ecef",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#6c757d",
                marginBottom: "8px",
              }}
            >
              Tài khoản đã lưu
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#2c3e50",
                marginBottom: "12px",
              }}
            >
              {payload.email || "Người dùng Microsoft"}
            </div>
            <button
              onClick={() => handleLogin(false)}
              style={{
                backgroundColor: "#0078d4",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                transition: "background-color 0.2s",
              }}
            >
              <MicrosoftIcon size={20} color="white" />
              Đăng nhập với tài khoản này
            </button>
          </div>

          <button
            onClick={() => handleLogin(true)}
            style={{
              backgroundColor: "transparent",
              color: "#0078d4",
              border: "1px solid #0078d4",
              padding: "12px 24px",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "all 0.2s",
            }}
          >
            <MicrosoftIcon size={20} color="currentColor" />
            Đăng nhập với tài khoản mới
          </button>
        </div>
      ) : (
        <button
          onClick={() => handleLogin(false)}
          style={{
            backgroundColor: "#0078d4",
            color: "white",
            border: "none",
            padding: "14px 24px",
            borderRadius: "6px",
            fontSize: "16px",
            fontWeight: "500",
            cursor: "pointer",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            transition: "background-color 0.2s",
          }}
        >
          <MicrosoftIcon size={24} color="white" />
          Đăng nhập bằng Microsoft
        </button>
      )}
    </div>
  );
};

export default MicrosoftLogin;

